export type ItemCategory =
  | 'navigation'
  | 'crew'
  | 'provisions'
  | 'antiscorbutics'
  | 'fishing'
  | 'armaments'
  | 'misc';

export interface Item {
  id: string;
  name: string;
  category: ItemCategory;
  unit: string;
  price: number;
  description: string;
  unique?: boolean;  // unique items can only be owned in quantity 0 or 1
  essential?: boolean; // shown with an extra mark in the shop — strongly recommended
  notForSale?: boolean; // items obtained only through gameplay (fishing, whaling, etc.)
}

// Order within each category dictates display order in the shop.
export const ITEMS: Item[] = [
  // NAVIGATION ────────────────────────────────────────────────────────────────
  { id: 'compass',      name: 'Mariner\'s Compass', category: 'navigation', unit: 'each',   price: 30,  unique: true, essential: true,
    description: 'Required. Without it you have only the sun and the stars, and the stars do not show up in fog.' },
  { id: 'telescope',    name: 'Telescope',          category: 'navigation', unit: 'each',   price: 80,  unique: true, essential: true,
    description: 'The only way to spot a distant sail before it spots you. Skipping this is a decision you may regret.' },
  { id: 'sextant',      name: 'Sextant',            category: 'navigation', unit: 'each',   price: 150, unique: true,
    description: 'For taking a fix from sun or star. Without it your position guess can be hundreds of miles off.' },
  { id: 'sea_charts',   name: 'Sea Charts',         category: 'navigation', unit: 'set',    price: 60,  unique: true,
    description: 'Atlantic crossings, known shipping lanes, and the islands that have been put on paper. Not all of them have.' },

  // CREW ──────────────────────────────────────────────────────────────────────
  { id: 'sailor',       name: 'Hire Sailor',        category: 'crew',       unit: 'man',    price: 50,  essential: true,
    description: 'A signing bonus per man. Each sailor eats and drinks daily. You will need at least a handful.' },
  { id: 'ship_surgeon', name: 'Ship\'s Surgeon',    category: 'crew',       unit: 'man',    price: 300, unique: true,
    description: 'Treats wounds, amputates limbs, and slows scurvy deaths. Useless without a medicine chest.' },
  { id: 'master_gunner',name: 'Master Gunner',      category: 'crew',       unit: 'man',    price: 200, unique: true,
    description: 'Drilled in the Spanish navy. Improves cannon accuracy and reload speed.' },

  // PROVISIONS ────────────────────────────────────────────────────────────────
  { id: 'fresh_water',  name: 'Fresh Water',        category: 'provisions', unit: 'barrel', price: 1,   essential: true,
    description: 'One man-week. Goes foul after a month or so at sea.' },
  { id: 'hardtack',     name: 'Hardtack',           category: 'provisions', unit: 'sack',   price: 1,   essential: true,
    description: 'Twice-baked ship\'s bread. Cheap, dense, near-indestructible. Feeds one man about five days.' },
  { id: 'salted_pork',  name: 'Salted Pork',        category: 'provisions', unit: 'barrel', price: 3,
    description: 'Preserved meat in heavy brine. Feeds one man about eight days. The crew will thank you.' },
  { id: 'dried_beans',  name: 'Dried Beans',        category: 'provisions', unit: 'sack',   price: 1,
    description: 'Boring but filling. Feeds one man about six days.' },
  { id: 'rum',          name: 'Rum',                category: 'provisions', unit: 'cask',   price: 5,
    description: 'Keeps the crew loyal. Keeps the captain awake. +Morale.' },

  // ANTISCORBUTICS ────────────────────────────────────────────────────────────
  { id: 'lemons',       name: 'Lemons',             category: 'antiscorbutics', unit: 'crate',  price: 10, essential: true,
    description: 'Wards off scurvy. One crate is roughly a man-month of protection.' },
  { id: 'sauerkraut',   name: 'Sauerkraut',         category: 'antiscorbutics', unit: 'barrel', price: 8,
    description: 'Sour cabbage. Wards off scurvy and stinks up the hold.' },
  { id: 'lime_juice',   name: 'Lime Juice',         category: 'antiscorbutics', unit: 'jug',    price: 12,
    description: 'Concentrated. Two man-months per jug. Useless once the cork rots.' },

  // FISHING ───────────────────────────────────────────────────────────────────
  { id: 'fishing_rod',  name: 'Fishing Rod',        category: 'fishing',    unit: 'each',   price: 60,
    description: 'A bamboo rod and a hook. Cast from the rail for the chance at a fish or two. Buy a spare — they crack.' },
  { id: 'fishing_net',  name: 'Fishing Net',        category: 'fishing',    unit: 'each',   price: 60,
    description: 'A seine net you deploy over the side. Catches passively. Tears easily in foul weather.' },
  { id: 'spare_netting',name: 'Spare Netting',      category: 'fishing',    unit: 'bolt',   price: 25,
    description: 'For mending a torn net. Without this, a broken net is a lost net.' },
  { id: 'harpoon',      name: 'Harpoon',            category: 'fishing',    unit: 'each',   price: 30,
    description: 'Iron-tipped. For whales and other large game. A miss often means losing the harpoon to the deep.' },
  { id: 'bait',         name: 'Bait',               category: 'fishing',    unit: 'sack',   price: 10,
    description: 'Salt pork scraps. Improves the chance of a catch when fishing from a rod.' },
  // Catches — never in the shop, only obtained at sea.
  { id: 'fish_caught',  name: 'Fish',               category: 'fishing',    unit: 'portion', price: 0, notForSale: true,
    description: 'Caught fish, dressed for the pot. Each portion feeds one man for two days.' },
  { id: 'whale_meat',   name: 'Whale Meat',         category: 'fishing',    unit: 'portion', price: 0, notForSale: true,
    description: 'Cut and salted from a whale. Each portion feeds one man for ten days.' },

  // ARMAMENTS ─────────────────────────────────────────────────────────────────
  { id: 'cannon_6lb',   name: 'Cannon, 6-pounder',  category: 'armaments',  unit: 'each',   price: 200,
    description: 'A serviceable broadside gun. Light, easy to crew, modest range.' },
  { id: 'cannon_12lb',  name: 'Cannon, 12-pounder', category: 'armaments',  unit: 'each',   price: 400,
    description: 'Heavier and more terrible. Needs more men and more powder.' },
  { id: 'gunpowder',    name: 'Gunpowder',          category: 'armaments',  unit: 'keg',    price: 10,
    description: 'Without this, your cannons are heavy decoration and your muskets are clubs.' },
  { id: 'round_shot',   name: 'Round Shot',         category: 'armaments',  unit: '10 rounds', price: 1,
    description: 'Solid iron ball. The standard for hulling another ship.' },
  { id: 'grape_shot',   name: 'Grape Shot',         category: 'armaments',  unit: '10 rounds', price: 3,
    description: 'A canvas bag of musket balls. For sweeping the enemy\'s deck.' },
  { id: 'chain_shot',   name: 'Chain Shot',         category: 'armaments',  unit: '10 rounds', price: 5,
    description: 'Two halves of a ball joined by chain. Tears through rigging and sails.' },
  { id: 'musket',       name: 'Musket',             category: 'armaments',  unit: 'each',   price: 15,
    description: 'For your boarding party. Slow to reload, but it works.' },
  { id: 'pistol',       name: 'Pistol',             category: 'armaments',  unit: 'each',   price: 20,
    description: 'One shot, then a club. Officers carry pairs.' },
  { id: 'cutlass',      name: 'Cutlass',            category: 'armaments',  unit: 'each',   price: 8,
    description: 'Short, heavy, cheap. The argument of last resort.' },
  { id: 'musket_balls', name: 'Musket Balls',       category: 'armaments',  unit: '20 rounds', price: 1,
    description: 'Lead balls for muskets and pistols. Useless without powder.' },
  { id: 'boarding_grapples', name: 'Boarding Grapples', category: 'armaments', unit: 'set', price: 40,
    description: 'Iron hooks on long ropes. Snag a passing ship\'s rail and swing across with cutlass in hand. One set per boarding.' },

  // MISC ──────────────────────────────────────────────────────────────────────
  { id: 'medicine_chest',  name: 'Medicine Chest',   category: 'misc', unit: 'each', price: 100, unique: true,
    description: 'Bandages, tinctures, a bonesaw, and a small mercy of laudanum. The surgeon needs this.' },
  { id: 'carpenters_tools',name: 'Carpenter\'s Tools', category: 'misc', unit: 'set',  price: 220,
    description: 'For patching the hull. The hull will need patching. Bring a spare set — saws snap and chisels dull.' },
  { id: 'lumber',          name: 'Lumber & Nails',   category: 'misc', unit: 'bundle', price: 35,
    description: 'Oak planks, spikes, and nails. Each bundle patches a measure of hull when the carpenter sets to work.' },
  { id: 'spare_sailcloth', name: 'Spare Sailcloth',  category: 'misc', unit: 'bolt', price: 40,
    description: 'For when a sail tears. Sails always tear.' },
  { id: 'spare_cordage',   name: 'Spare Cordage',    category: 'misc', unit: 'coil', price: 30,
    description: 'For rigging repairs.' },
  { id: 'ships_cat',       name: 'Ship\'s Cat',      category: 'misc', unit: 'each', price: 5,   unique: true,
    description: 'Kills rats. Lifts crew morale. Universally beloved.' },
];

export const ITEMS_BY_ID: Map<string, Item> = new Map(ITEMS.map(i => [i.id, i]));

export function getItem(id: string): Item | undefined {
  return ITEMS_BY_ID.get(id);
}
